import { business, enterprise, getPlanInclusionCopy, starter, team } from "../../lib/src/common/plans";
import * as toolTipStyles from "./FeaturesMatrix/LabelCell.module.scss";
import { Price, symbolForPlanCurrency } from "./PricingPlan/Price/Price";
import { PricingPlan } from "./PricingPlan/PricingPlan";
import * as styles from "./PricingPlans.module.scss";
import * as Tooltip from "@radix-ui/react-tooltip";
import * as React from "react";

export type IBillingCycle = "monthly" | "yearly";
export type Option = { label: string; value: string };
type PlansProps = {
  discount: string;
  showDiscount: boolean;
  period: IBillingCycle;
  currency: { name: string; label: string };
  notForProfitActivated: boolean;
  handleTeamClick: () => void;
  handleStarterClick: () => void;
  handleEnterpriseClick: () => void;
  handleBusinessClick: () => void;
  handlePeriodChange: (value: boolean) => void;
};

export const PricingPlans = (props: PlansProps): JSX.Element => {
  const {
    currency,
    period,
    discount,
    handleTeamClick,
    handleStarterClick,
    handleEnterpriseClick,
    handleBusinessClick,
    handlePeriodChange,
    showDiscount,
    notForProfitActivated,
  } = props;

  const getPlanPrice = (plan, priceType, period, currency, notForProfitActivated) => {
    const priceStructure = plan?.marketing?.prices?.[priceType];
    if (!priceStructure) {
      return undefined;
    }

    const currencyKey = currency.label;
    const periodPrice = priceStructure[period]?.[currencyKey];
    const fallbackPrice = priceStructure.monthly?.[currencyKey];
    const basePrice = typeof periodPrice === "number" ? periodPrice : typeof fallbackPrice === "number" ? fallbackPrice : undefined;

    const shouldApplyNotForProfitPricing = notForProfitActivated && plan !== starter;

    if (shouldApplyNotForProfitPricing) {
      const nfpPrice = priceStructure.nfp?.[currencyKey];
      if (typeof nfpPrice === "number") {
        return nfpPrice;
      }

      if (typeof basePrice === "number") {
        return Math.round(basePrice * 0.6 * 100) / 100;
      }

      return undefined;
    }

    return basePrice;
  };

  const starterPrice = getPlanPrice(starter, "planPrice", period, currency, notForProfitActivated);
  const teamPrice = getPlanPrice(team, "planPrice", period, currency, notForProfitActivated);
  const businessPrice = getPlanPrice(business, "planPrice", period, currency, notForProfitActivated);

  const starterAdditionalUserPrice = getPlanPrice(starter, "userAddonPrice", period, currency, notForProfitActivated);
  const teamAdditionalStoryPrice = getPlanPrice(team, "publishAddonPrice", "monthly", currency, notForProfitActivated);
  const businessAdditionalStoryPrice = getPlanPrice(business, "publishAddonPrice", "monthly", currency, notForProfitActivated);
  const starterAdditionalStoryPrice = getPlanPrice(starter, "publishAddonPrice", "monthly", currency, notForProfitActivated);

  const contactSalesText = "Contact Sales";
  const isYearly = period === "yearly";
  const symbol = symbolForPlanCurrency[currency.label];
  const periodLabel = "month";
  const includesLabel = "Includes:";

  const seatCopyOptions = { includeIncludedWord: false };
  const { seatCopy: starterSeatCopy, storyCopy: starterStoryCopy } = getPlanInclusionCopy(starter, seatCopyOptions);
  const { seatCopy: teamSeatCopy, storyCopy: teamStoryCopy } = getPlanInclusionCopy(team, seatCopyOptions);
  const { seatCopy: businessSeatCopy, storyCopy: businessStoryCopy } = getPlanInclusionCopy(business, seatCopyOptions);
  const getStoryTooltipText = (storyInclusions?: number) =>
    typeof storyInclusions === "number"
      ? `${storyInclusions} publishing credits are delivered annually on your plan anniversary, regardless of whether you’re billed monthly or yearly.`
      : "Publishing credits are delivered annually on your plan anniversary, regardless of whether you’re billed monthly or yearly.";
  const StoryCopy = ({
    copy,
    storyInclusions,
    additionalStoryPrice,
    additionalStoryPriceTestId,
    showAdditionalStoryPrice = false,
  }: {
    copy?: string;
    storyInclusions?: number;
    additionalStoryPrice?: number;
    additionalStoryPriceTestId?: string;
    showAdditionalStoryPrice?: boolean;
  }) =>
    copy ? (
      <p className={styles.paragraph}>
        <span className={styles.storyCopy}>
          <span>
            {copy}
            {showAdditionalStoryPrice && typeof additionalStoryPrice === "number" && (
              <>
                <br />(
                <span data-testid={additionalStoryPriceTestId}>
                  {symbol}
                  {additionalStoryPrice}
                </span>
                &nbsp;one-off payment per additional story)
              </>
            )}
          </span>
          <Tooltip.Provider delayDuration={800} skipDelayDuration={500}>
            <Tooltip.Root>
              <Tooltip.Trigger asChild>
                <button type="button" aria-label="Story inclusions details" className={styles.storyTooltipTrigger}>
                  <svg width="14" height="14" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" viewBox="0 0 512 512">
                    <path
                      fill="#a3a3a3"
                      d="M464 256a208 208 0 1 0-416 0 208 208 0 1 0 416 0zM0 256a256 256 0 1 1 512 0 256 256 0 1 1-512 0zm169.8-90.7c7.9-22.3 29.1-37.3 52.8-37.3h58.3c34.9 0 63.1 28.3 63.1 63.1 0 22.6-12.1 43.5-31.7 54.8L280 264.4c-.2 13-10.9 23.6-24 23.6-13.3 0-24-10.7-24-24v-13.5c0-8.6 4.6-16.5 12.1-20.8l44.3-25.4c4.7-2.7 7.6-7.7 7.6-13.1 0-8.4-6.8-15.1-15.1-15.1h-58.3c-3.4 0-6.4 2.1-7.5 5.3l-.4 1.2c-4.4 12.5-18.2 19-30.6 14.6s-19-18.2-14.6-30.6l.4-1.2zM224 352a32 32 0 1 1 64 0 32 32 0 1 1-64 0z"
                    />
                  </svg>
                </button>
              </Tooltip.Trigger>
              <Tooltip.Content sideOffset={4} className={toolTipStyles.TooltipContent}>
                {getStoryTooltipText(storyInclusions)}
                <Tooltip.Arrow className={toolTipStyles.TooltipArrow} />
              </Tooltip.Content>
            </Tooltip.Root>
          </Tooltip.Provider>
        </span>
      </p>
    ) : null;

  return (
    <div className={styles.planRow}>
      <PricingPlan
        theme="dark"
        showDiscount={showDiscount}
        discount={discount}
        ctaText={isYearly ? contactSalesText : "Get Started"}
        period={period}
        plan={starter}
        ctaFunction={handleStarterClick}
        switchFunction={handlePeriodChange}
      >
        <div>
          <Price period={period} userCurrency={currency} theme={"dark"} dynamicPrice={starterPrice}></Price>
          <p className={styles.paragraph}>{includesLabel}</p>
          {starterSeatCopy && (
            <p className={styles.paragraph}>
              {starterSeatCopy}
              {typeof starterAdditionalUserPrice === "number" && (
                <>
                  <br />({symbol}
                  {starterAdditionalUserPrice}/{periodLabel} per additional member)
                </>
              )}
            </p>
          )}
          <StoryCopy
            copy={starterStoryCopy}
            storyInclusions={starter.marketing?.storyInclusions}
            additionalStoryPrice={starterAdditionalStoryPrice}
            additionalStoryPriceTestId="starter-story-addon-price"
            showAdditionalStoryPrice
          />
        </div>
      </PricingPlan>
      <PricingPlan
        showDiscount={showDiscount}
        theme="dark"
        discount={discount}
        ctaText={isYearly ? contactSalesText : "Get Started"}
        period={period}
        plan={team}
        ctaFunction={handleTeamClick}
        switchFunction={handlePeriodChange}
      >
        <div>
          <Price period={period} userCurrency={currency} theme={"dark"} dynamicPrice={teamPrice}></Price>
          <p className={styles.paragraph}>{includesLabel}</p>
          {teamSeatCopy && <p className={styles.paragraph}>{teamSeatCopy}</p>}
          <StoryCopy
            copy={teamStoryCopy}
            storyInclusions={team.marketing?.storyInclusions}
            additionalStoryPrice={teamAdditionalStoryPrice}
            additionalStoryPriceTestId="team-story-addon-price"
            showAdditionalStoryPrice={!isYearly}
          />
          {isYearly ? (
            <p className={styles.paragraph}>Additional stories at volume pricing</p>
          ) : (
            <p className={styles.paragraph}>Options for volume pricing on annual plans</p>
          )}
        </div>
      </PricingPlan>
      <PricingPlan
        theme="dark"
        showDiscount={showDiscount}
        discount={discount}
        ctaText={isYearly ? contactSalesText : "Get Started"}
        period={period}
        plan={business}
        ctaFunction={handleBusinessClick}
        switchFunction={handlePeriodChange}
      >
        <div>
          <Price period={period} userCurrency={currency} theme={"dark"} dynamicPrice={businessPrice}></Price>
          <p className={styles.paragraph}>{includesLabel}</p>
          {businessSeatCopy && <p className={styles.paragraph}>{businessSeatCopy}</p>}
          <StoryCopy
            copy={businessStoryCopy}
            storyInclusions={business.marketing?.storyInclusions}
            additionalStoryPrice={businessAdditionalStoryPrice}
            additionalStoryPriceTestId="business-story-addon-price"
            showAdditionalStoryPrice={!isYearly}
          />
          {isYearly ? (
            <p className={styles.paragraph}>Additional stories at volume pricing</p>
          ) : (
            <p className={styles.paragraph}>Options for volume pricing on annual plans</p>
          )}
        </div>
      </PricingPlan>
      <PricingPlan
        showDiscount={showDiscount}
        ctaText={isYearly ? contactSalesText : "Contact Sales"}
        discount={discount}
        theme="dark"
        period={period}
        plan={enterprise}
        ctaFunction={handleEnterpriseClick}
        switchFunction={handlePeriodChange}
      >
        <div>
          <div className={styles.contactWrapperAligned}>
            <p className={styles.contactUs}>Contact Us</p>
          </div>
          <p className={styles.paragraph} style={{ marginTop: 0 }}>
            {includesLabel}
          </p>
          <p className={styles.paragraph}>A plan and terms tailored to your requirements</p>
          <p className={styles.paragraph}>Volume pricing</p>
          <p className={styles.paragraph}>Battle-tested security and compliance</p>
          <p className={styles.paragraph}>Options for custom development</p>
          <p className={styles.paragraph}>Options for custom design services</p>
        </div>
      </PricingPlan>
    </div>
  );
};
